import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  ProgressBar,
  DescriptionList,
  DataTable,
  Spinner,
} from '@shopify/polaris';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';

const WAREHOUSE_APP_URL = (import.meta.env.VITE_WAREHOUSE_APP_URL as string | undefined) || 'https://warehouse.whenlyapp.com';

interface PickListItem {
  id: number;
  sku: string | null;
  title: string | null;
  quantity: number;
  pickedQuantity: number;
  locationCode: string;
  orderNumber: string | number | null;
  barcode: string | null;
}

interface PickList {
  id: number;
  name: string | null;
  reference: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  warehouse_id: number | null;
  assigned_to: string | null;
  assigned_user_id: number | null;
  notes: string | null;
  order_ids: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  items: PickListItem[];
}

type PickListStatus = PickList['status'];

const statusConfig: Record<PickListStatus, { tone: 'success' | 'warning' | 'info' | 'critical'; label: string }> = {
  pending: { tone: 'warning', label: 'Pending' },
  in_progress: { tone: 'info', label: 'In Progress' },
  completed: { tone: 'success', label: 'Completed' },
  cancelled: { tone: 'critical', label: 'Cancelled' },
};

function formatDate(value: string | null) {
  if (!value) return '-';
  const date = new Date(value.includes('T') || value.includes('Z') ? value : `${value.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function orderCount(orderIds: string | null | undefined, items: PickListItem[]) {
  try {
    const parsed = JSON.parse(orderIds || '[]');
    if (Array.isArray(parsed) && parsed.length) return parsed.length;
  } catch { /* fall through to the line items */ }
  return new Set(items.map(item => String(item.orderNumber ?? ''))).size;
}

export default function PickListDetail() {
  const { pickListId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { fetchApi, error } = useApi();
  const [pickList, setPickList] = useState<PickList | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  useEffect(() => {
    void loadPickList();
  }, [pickListId]);

  async function loadPickList() {
    setLoading(true);
    setNotFound(false);
    const data = await fetchApi<{ pickList: PickList }>(`/picking/lists/${pickListId}`);
    if (data?.pickList) setPickList(data.pickList);
    else setNotFound(true);
    setLoading(false);
  }

  const runAction = async (path: string, successMessage: string) => {
    setActionLoading(true);
    setActionMessage(null);
    const result = await fetchApi<{ success: boolean }>(`/picking/lists/${pickListId}/${path}`, { method: 'POST' });
    if (result?.success) setActionMessage(successMessage);
    await loadPickList();
    setActionLoading(false);
  };

  if (loading && !pickList) {
    return (
      <Page title="Pick list" backAction={{ content: 'Pick Lists', onAction: () => navigate('/pick-lists') }}>
        <Card>
          <InlineStack align="center" blockAlign="center" gap="200">
            <Spinner size="small" />
            <Text as="p">Loading pick list…</Text>
          </InlineStack>
        </Card>
      </Page>
    );
  }

  if (!pickList) {
    return (
      <Page title="Pick list" backAction={{ content: 'Pick Lists', onAction: () => navigate('/pick-lists') }}>
        <Banner tone="critical">
          <p>{notFound ? 'This pick list could not be found. It may belong to another store or have been removed.' : 'This pick list could not be loaded.'}</p>
          {error && <p>{error}</p>}
        </Banner>
      </Page>
    );
  }

  const items = pickList.items || [];
  const totalQty = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const pickedQty = items.reduce((sum, item) => sum + Math.min(Number(item.pickedQuantity || 0), Number(item.quantity || 0)), 0);
  const progress = totalQty > 0 ? Math.round((pickedQty / totalQty) * 100) : 0;
  const allPicked = totalQty > 0 && pickedQty >= totalQty;
  const open = pickList.status === 'pending' || pickList.status === 'in_progress';
  const status = statusConfig[pickList.status] || { tone: 'info' as const, label: pickList.status };
  const canPick = hasPermission('picking.lists.pick');
  const canComplete = hasPermission('picking.lists.complete');

  const secondaryActions = [];
  if (open && canPick) {
    secondaryActions.push({ content: 'Open in Warehouse App', url: `${WAREHOUSE_APP_URL}/pick/${pickList.id}`, external: true });
  }

  const primaryAction = pickList.status === 'pending' && canPick
    ? { content: 'Start picking', loading: actionLoading, onAction: () => runAction('start', 'Picking started.') }
    : open && canComplete
      ? { content: 'Complete pick list', loading: actionLoading, disabled: !allPicked, onAction: () => runAction('complete', 'Pick list completed.') }
      : undefined;

  return (
    <Page
      title={pickList.reference || pickList.name || `Pick-${pickList.id}`}
      titleMetadata={<Badge tone={status.tone}>{status.label}</Badge>}
      backAction={{ content: 'Pick Lists', onAction: () => navigate('/pick-lists') }}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}
        {actionMessage && !error && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setActionMessage(null)}>{actionMessage}</Banner>
          </Layout.Section>
        )}
        {open && canComplete && !allPicked && (
          <Layout.Section>
            <Banner tone="info">
              Picking is recorded item by item in the Warehouse App. This list can be completed once every reserved unit has been picked.
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Items to pick</Text>
                <Text as="span" tone="subdued">{pickedQty} of {totalQty} units picked</Text>
              </InlineStack>
              <ProgressBar progress={progress} tone={allPicked ? 'success' : 'primary'} size="small" />
              {items.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'text']}
                  headings={['Location', 'SKU', 'Product', 'Order', 'Required', 'Picked', 'Status']}
                  rows={items.map(item => {
                    const required = Number(item.quantity || 0);
                    const picked = Number(item.pickedQuantity || 0);
                    const badge = required > 0 && picked >= required
                      ? <Badge tone="success">Picked</Badge>
                      : picked > 0
                        ? <Badge tone="attention">Partial</Badge>
                        : <Badge>Pending</Badge>;
                    return [
                      item.locationCode || <Text as="span" tone="subdued">No bin</Text>,
                      item.sku || '-',
                      item.title || '-',
                      item.orderNumber ? `#${item.orderNumber}` : '-',
                      required.toString(),
                      picked.toString(),
                      badge,
                    ];
                  })}
                />
              ) : (
                <Text as="p" tone="subdued">This pick list has no items.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Details</Text>
              <DescriptionList
                items={[
                  { term: 'Reference', description: pickList.reference || `Pick-${pickList.id}` },
                  { term: 'Orders', description: String(orderCount(pickList.order_ids, items)) },
                  { term: 'Lines', description: String(items.length) },
                  { term: 'Assigned to', description: pickList.assigned_to || (pickList.assigned_user_id ? `User #${pickList.assigned_user_id}` : 'Unassigned') },
                  { term: 'Created', description: formatDate(pickList.created_at) },
                  { term: 'Started', description: formatDate(pickList.started_at) },
                  { term: 'Completed', description: formatDate(pickList.completed_at) },
                ]}
              />
              {pickList.notes && (
                <BlockStack gap="100">
                  <Text as="h3" variant="headingSm">Notes</Text>
                  <Text as="p">{pickList.notes}</Text>
                </BlockStack>
              )}
              {open && (
                <Button url={`${WAREHOUSE_APP_URL}/pick/${pickList.id}`} external fullWidth>
                  Pick on a handheld device
                </Button>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
